import React, { useEffect, useMemo, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3120CustomerFormExtendedHandlers } from "../../../U3120CustomerFormTypes";
import { u3120CustomerEmployeeRateHandler } from "./U3120CustomerEmployeeRateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS, U3120ROLES } from "../../../../U3120";
import { emptyCustomerEmployeeRate, type U3120CustomerEmployeeRate } from "../../../../U3120Service";
import U3120CustomerEmployeeRateDeleteDlg from "./U3120CustomerEmployeeRateDeleteDlg";
import U3120CustomerEmployeeRateEditDlg from "./U3120CustomerEmployeeRateEditDlg";
import { U2000CurrencyDisplay } from "../../../../../u2000Core/components/tables/display/U2000CurrencyDisplay";
import { u3120ValidateCustomerEmployeeRate } from "../../../../tools/U3120CustomerEmployeeRateValidation";
import { u3120EmployeeRateActionsDisabled } from "../../../../tools/U3120CustomerEmployeeRateActions";

// Typage des props du composant
interface U3120CustomerEmployeeRatesComponentProps {
  u3120CustomerFormHandlers: U3120CustomerFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des taux horaires d'un client
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "employee",
      label: t("lblEmployee", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      type: "string",
      align: "left",
      display: (_column: any, value: any, _row: any) => {
        if (!value) return "";
        const parts = [];
        if (value.firstName || value.lastName) {
          parts.push(`${value.firstName || ""} ${value.lastName || ""}`.trim());
        }
        if (value.codeEmployee) {
          parts.push(`(${value.codeEmployee})`);
        }
        return parts.length > 0 ? parts.join(" ") : value.id || "";
      },
    },
    {
      name: "hourRate",
      label: t("lblHourRate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[150px]",
      type: "currency",
      align: "right",
      display: (_column: any, value: any, _row: any) => {
        if (value === null || value === undefined) return <span className="text-gray-500">—</span>;
        return <U2000CurrencyDisplay value={value} align="right" />;
      },
    },
    {
      name: "validFrom",
      label: t("lblValidFrom", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      display: (_column: any, _value: any, row: U3120CustomerEmployeeRate) => row.validFrom ?? "",
    },
    {
      name: "validTo",
      label: t("lblValidTo", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      display: (_column: any, _value: any, row: U3120CustomerEmployeeRate) => row.validTo ?? "",
    },
  ];
}

// Composant principal pour la gestion des taux horaires d'un client
const U3120CustomerEmployeeRateBody: React.FC<U3120CustomerEmployeeRatesComponentProps> = ({ u3120CustomerFormHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U3120CustomerEmployeeRate>();

  const validateCustomerEmployeeRate = (customerEmployeeRate: U3120CustomerEmployeeRate) =>
    u3120ValidateCustomerEmployeeRate(customerEmployeeRate, gridStateHandlers.getData(), t);

  const u3120CustomerEmployeeRateBL: U2000FormSBusinessHandlers<U3120CustomerEmployeeRate> = {
    newRecord: () => {
      return emptyCustomerEmployeeRate;
    },

    // validation du formulaire
    validateForm: validateCustomerEmployeeRate,
  };

  const u3120CustomerEmployeeRateformStateHandlers = useU2000FormStateHandlers<U3120CustomerEmployeeRate>({
    formState,
    formStateDispatch,
    businessHandlers: u3120CustomerEmployeeRateBL,
  });

  const u3120CustomerEmployeeRateHandlers = u3120CustomerEmployeeRateHandler({ gridStateHandlers, t, formStateHandlers: u3120CustomerEmployeeRateformStateHandlers });

  // Transposer les données du formStateHandlers au gridStateHandlers pour CustomerEmployeeRates
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const customerEmployeeRates = u3120CustomerFormHandlers.getData?.()?.customerEmployeeRates;
    gridStateHandlers.setData(Array.isArray(customerEmployeeRates) ? customerEmployeeRates : []);
    gridStateHandlers.setTotalRows(Array.isArray(customerEmployeeRates) ? customerEmployeeRates.length : 0);

    // Gérer les erreurs spécifiques au champ CustomerEmployeeRates
    const errorsField = u3120CustomerFormHandlers.getOtherErrors()?.customerEmployeeRates;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u3120CustomerFormHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u3120CustomerEmployeeRateHandlers.dataChangedEvent === 0) return;

    const customerEmployeeRates = gridStateHandlers.getData();
    u3120CustomerFormHandlers.setData({
      ...u3120CustomerFormHandlers.getData?.(),
      customerEmployeeRates: customerEmployeeRates,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u3120CustomerEmployeeRateHandlers.dataChangedEvent]);

  const isRead = u3120CustomerFormHandlers.getFormState?.() === U2000FormStateEnumState.READ;
  const selectedRows = gridStateHandlers.getSelectedRows();
  const hasViewEmployeeRate = hasRole(U3120ROLES.VIEWEMPLOYEERATE);
  const { addDisabled, modifyDisabled, deleteDisabled } = u3120EmployeeRateActionsDisabled({
    isRead,
    canViewRates: hasViewEmployeeRate,
    selectedCount: selectedRows.length,
  });

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u3120CustomerEmployeeRateHandlers.onAdd,
        hasRole: hasRole(U3120ROLES.EDIT),
        disabled: addDisabled,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3120CustomerEmployeeRateHandlers.onModify,
        hasRole: hasRole(U3120ROLES.EDIT),
        disabled: modifyDisabled,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u3120CustomerEmployeeRateHandlers.onDelete,
        hasRole: hasRole(U3120ROLES.EDIT),
        disabled: deleteDisabled,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3120CustomerEmployeeRateHandlers, addDisabled, modifyDisabled, deleteDisabled],
  );

  // Render
  return (
    <>
      <U3120CustomerEmployeeRateEditDlg u3120CustomerEmployeeRateHandler={u3120CustomerEmployeeRateHandlers} t={t} NS={NS} />
      <U3120CustomerEmployeeRateDeleteDlg u3120CustomerEmployeeRateHandler={u3120CustomerEmployeeRateHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u3120CustomerFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblCustomerEmployeeRate", {
          count: u3120CustomerEmployeeRateHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3120CustomerEmployeeRateBody;
